import { useComputed, useModal } from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import React, { createContext, useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router";
import {
  DesignPackageLocation,
  SaveDesignPackageInput,
  SaveDesignPackageOptionInput,
  SaveDesignPackageOptionPrereqInput,
} from "src/generated/graphqlTypes";
import { DesignPackageItemSlotEditorDocument } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageItemSlotEditor.generated";
import {
  DesignPackageGlobalOptionGroupFragment,
  EditDesignPackage_DesignPackageFragment,
  NewDesignPackage_ReadyPlanOptionFragment,
  useDesignPackageGlobalOptionsQuery,
  useEditDesignPackageQuery,
  useMakeNewDesignPackageMutation,
  usePreselectOptionsFromPlanPackagesQuery,
} from "src/routes/libraries/design-catalog/new-design-catalog-wizard/NewDesignPackage.generated";
import { createDesignPackageUrl } from "src/RouteUrls";
import { fail } from "src/utils";

// Export for testing
export type NewDesignPackageContextValue = {
  formState: ObjectState<NewDesignPackageForm>;
  inFlight: boolean;
  save: () => Promise<void>;
  globalOptionGroups: DesignPackageGlobalOptionGroupFragment[];
  planPackageOptions: NewDesignPackage_ReadyPlanOptionFragment[];
  /** Denotes Create vs Edit. Expectation: Controls Interior/Exterior on create, which can no longer be updated. */
  isEditMode: boolean;
};

// Export for testing
export const NewDesignPackageContext = createContext<NewDesignPackageContextValue>({
  formState: undefined!, // This should init immediately. Only reason undefined would trigger is if Provider isn't actually wrapping a component when useContext is called, which should never happen
  inFlight: false,
  save: async () => {},
  globalOptionGroups: [],
  planPackageOptions: [],
  isEditMode: false,
});

export const useNewDesignPackageContext = () => React.useContext(NewDesignPackageContext);

type NewDesignPackageProviderProps = React.PropsWithChildren<{
  maybeDesignPackageId: string | undefined;
  maybeVersionId: string | undefined;
}>;

export function NewDesignPackageProvider(props: NewDesignPackageProviderProps) {
  const { children, maybeDesignPackageId, maybeVersionId } = props;
  const { data: maybeDesignPackage } = useEditDesignPackageQuery({
    skip: !maybeDesignPackageId,
    variables: { id: maybeDesignPackageId!, versionId: maybeVersionId },
  });

  const isEditMode = useMemo(() => !!maybeDesignPackageId, [maybeDesignPackageId]);

  const formState = useFormState({
    config: newDesignPackageFormConfig,
    init: {
      input: maybeDesignPackage?.designPackage,
      // maybeDesignPackage query won't fire during the create-new flow, so map never runs. Use this as a fallback.
      ifUndefined: {
        name: "",
        planPackageIds: [],
        // forces the user to pick
        location: maybeDesignPackage?.designPackage.location.code ?? undefined,
        markets: [],
        options: [],
      },
      map: mapEditDesignPackageToForm,
    },
  });
  const [mutate, { loading: inFlight }] = useMakeNewDesignPackageMutation();
  const { closeModal } = useModal();
  const location =
    useComputed(() => formState.location.value, [formState]) ?? maybeDesignPackage?.designPackage.location.code;
  const { data, loading: optionsLoading } = useDesignPackageGlobalOptionsQuery({
    variables: {
      filter: {
        version: [2],
        ...(location === DesignPackageLocation.Interior && { forDesignInterior: true }),
        ...(location === DesignPackageLocation.Exterior && { forDesignExterior: true }),
      },
    },
    skip: !location,
  });
  const history = useHistory();

  /** A DesignPackage should pre-select any options from PlanPackages */
  const planPackageIds = useComputed(() => formState.planPackageIds.value ?? [], [formState]);
  const preselectQuery = usePreselectOptionsFromPlanPackagesQuery({
    variables: { planPackageIds },
    skip: planPackageIds.isEmpty || location !== DesignPackageLocation.Exterior,
  });
  const ppElevationRpos = useMemo(
    () =>
      preselectQuery.data?.planPackages.entities.flatMap((pp) =>
        pp.optionGroups.filter((rpog) => rpog.group.type.isElevation).flatMap((og) => og.options),
      ) ?? [],
    [preselectQuery],
  );
  /** preselect elevation options from plan packages */
  useEffect(() => {
    if (ppElevationRpos.nonEmpty) {
      const existingOptions = maybeDesignPackage?.designPackage.options ?? [];
      // Combine the existing options with the preselected ones
      // They will be grouped by global option id + name, so we can display them together
      // When saving, we'll skip the id since the BE knows how to search for existing options.
      formState.options.set(mapOptionsToForm([...existingOptions, ...ppElevationRpos], location, ppElevationRpos));
    }
  }, [ppElevationRpos, location, formState, maybeDesignPackage?.designPackage.options]);

  const save = useCallback(async () => {
    const { location, options, ...values } = formState.value;
    const result = await mutate({
      variables: {
        input: {
          ...values,
          // Update this design package if we're editing an existing one
          ...(maybeDesignPackage && { id: maybeDesignPackageId }),
          // only touch location if it's a new design package
          ...(!maybeDesignPackage && { location }),
          // filter out options added but not fully filled out
          options: options
            ?.filter((o) => o.id || o.name)
            .map((o) => {
              const { globalOptionId, ...rest } = o;
              return {
                ...rest,
                // Preselected options have been grouped by global option id, and the rpo id has been faked to be unique
                // For those, the GlobalOptionId will be used as the key to findOrCreate on BE
                id: o.id?.startsWith("rpo:") ? o.id : undefined,
                // Also remove globalOptionId from children
                optionPrereqChildren: o.optionPrereqChildren?.map(({ globalOptionId, ...child }) => child),
              };
            }),
        },
      },
      refetchQueries: [DesignPackageItemSlotEditorDocument],
    });
    const newId = result.data?.saveDesignPackage.designPackage.id ?? fail();
    const versionId = result.data?.saveDesignPackage.designPackage.version.id ?? fail();
    history.push(createDesignPackageUrl(newId, versionId));
    closeModal();
  }, [maybeDesignPackage, mutate, formState.value, maybeDesignPackageId, history, closeModal]);

  const globalOptionGroups = useMemo(() => (data?.options ?? []).sortBy((gog) => gog.type.order), [data]);

  return (
    <NewDesignPackageContext.Provider
      value={{
        formState,
        inFlight: inFlight || optionsLoading,
        save,
        globalOptionGroups,
        isEditMode,
        planPackageOptions: ppElevationRpos,
      }}
    >
      {children}
    </NewDesignPackageContext.Provider>
  );
}

/** Internal type for form state that includes globalOptionId for UI logic */
type DesignPackageOptionPrereqFormInput = SaveDesignPackageOptionPrereqInput & { globalOptionId?: string };

export type DesignPackageOptionFormInput = Omit<SaveDesignPackageOptionInput, "optionPrereqChildren"> & {
  globalOptionId?: string;
  optionPrereqChildren?: DesignPackageOptionPrereqFormInput[];
};

/** Form State to span multiple pages */
export type NewDesignPackageForm = Omit<SaveDesignPackageInput, "options"> & {
  options?: DesignPackageOptionFormInput[];
};

const optionPrereqConfig: ObjectConfig<DesignPackageOptionPrereqFormInput> = {
  id: { type: "value" },
  name: { type: "value" },
  globalOptionGroupId: { type: "value" },
  globalOptionId: { type: "value" },
};

const optionConfig: ObjectConfig<DesignPackageOptionFormInput> = {
  id: { type: "value" },
  name: { type: "value" },
  globalOptionGroupId: { type: "value" },
  globalOptionId: { type: "value" },
  optionPrereqChildren: { type: "list", config: optionPrereqConfig },
};

export const newDesignPackageFormConfig: ObjectConfig<NewDesignPackageForm> = {
  name: { type: "value", rules: [required] },
  planPackageIds: { type: "value", rules: [required] },
  location: { type: "value", rules: [required] },
  markets: { type: "value" },
  ownerId: { type: "value" },
  options: {
    type: "list",
    config: optionConfig,
  },
};

function isInteriorOption(
  rpo: NewDesignPackage_ReadyPlanOptionFragment,
  location: DesignPackageLocation | undefined | null,
) {
  return location === DesignPackageLocation.Interior && rpo.globalOption.group.forDesignInterior;
}

function isExteriorOption(
  rpo: NewDesignPackage_ReadyPlanOptionFragment,
  location: DesignPackageLocation | undefined | null,
) {
  return location === DesignPackageLocation.Exterior && rpo.globalOption.group.forDesignExterior;
}

// Export for testing
export function mapEditDesignPackageToForm(dp: EditDesignPackage_DesignPackageFragment) {
  return {
    name: dp.name,
    planPackageIds: dp.planPackages.map(({ id }) => id) ?? [],
    markets: dp.markets.map(({ id }) => id) ?? [],
    options: mapOptionsToForm(dp.options, dp.location.code, []),
    ownerId: dp.owner?.id,
  };
}

function mapOptionsToForm(
  rpos: NewDesignPackage_ReadyPlanOptionFragment[],
  location: DesignPackageLocation | undefined | null,
  selectedPlanPackageOptions: NewDesignPackage_ReadyPlanOptionFragment[],
) {
  const childrenOptionsById = rpos
    .filter((rpo) => rpo.active)
    .flatMap((rpo) => rpo.optionPrereqChildren)
    .uniqueByKey("id")
    .keyBy((rpo) => rpo.id);

  const ppRpos = selectedPlanPackageOptions.keyBy((o) => o.id);

  return (
    rpos
      // only display options that are interior or exterior
      .filter((rpo) => isInteriorOption(rpo, location) || isExteriorOption(rpo, location))
      .filter((rpo) => rpo.active)
      // filter out options that are children of other options
      .filter((rpo) => !childrenOptionsById[rpo.id])
      // sort by createdAt
      .sortBy((rpo) => rpo.createdAt)
      // When preselecting plan package options, we might get some global options duplicated.
      // Grouping by global option id will ensure we only display one of each pairing.
      .groupBy((rpo) => rpo.globalOption.id)
      .toValues()
      .map((rpos) => {
        // rpos is a mix of PP RPOs and DP RPOs, get just our rpos
        const dpRpos = rpos.filter((rpo) => !ppRpos[rpo.id]);
        const rpo = dpRpos.first ?? rpos.first!;
        // If this is not a preselected PP option, use the original rpo:ID
        if (dpRpos.nonEmpty) {
          return {
            id: rpo.id,
            name: rpo.name,
            globalOptionGroupId: rpo.globalOption.group.id,
            globalOptionId: rpo.globalOption.id,
            optionPrereqChildren: rpo.optionPrereqChildren,
          };
        }

        return {
          // This handles the preselected elevation options from plan packages.
          // We don't have a unique id for these, so keying by GlobalOptionId will ensure
          // to not display duplicated pairing combos.
          // When saving, we'll skip the id since the BE knows how to search for existing options.
          id: rpo.globalOption.id,
          globalOptionGroupId: rpo.globalOption.group.id,
          globalOptionId: rpo.globalOption.id,
          name: rpo.name,
          optionPrereqChildren: rpos.flatMap((rpo) => rpo.optionPrereqChildren).uniqueByKey("id"),
        };
      })
      .map((rpo) => ({
        ...rpo,
        optionPrereqChildren: rpo.optionPrereqChildren.map((rpoChild) => ({
          id: rpoChild.id,
          name: rpoChild.name,
          globalOptionGroupId: rpoChild.globalOption.group.id,
          globalOptionId: rpoChild.globalOption.id,
        })),
      }))
  );
}
